/**
 * AppUpdateBanner — banner sticky top che compare quando è disponibile una nuova
 * versione della PWA (service worker in stato `installed` con controller attivo).
 *
 * L'evento globale `ef-sw-update-available` viene dispatched da `src/index.js`
 * quando il SW rileva un aggiornamento. Al click su "Aggiorna ora" viene inviato
 * `postMessage({type: 'SKIP_WAITING'})` al SW in attesa; il conseguente
 * `controllerchange` (gestito in index.js) triggera un `window.location.reload()`.
 */
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

export default function AppUpdateBanner() {
  const [available, setAvailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onUpdate = () => setAvailable(true);
    window.addEventListener("ef-sw-update-available", onUpdate);
    return () => window.removeEventListener("ef-sw-update-available", onUpdate);
  }, []);

  if (!available) return null;

  const applyUpdate = async () => {
    setRefreshing(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/");
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
      } else {
        // Fallback: forza il reload
        window.location.reload();
      }
    } catch {
      window.location.reload();
    }
  };

  return (
    <div
      data-testid="app-update-banner"
      className="fixed top-0 inset-x-0 z-[9999] bg-primary text-primary-foreground shadow-lg"
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
        <RefreshCw
          className={`w-4 h-4 flex-shrink-0 ${refreshing ? "animate-spin" : ""}`}
          strokeWidth={1.5}
        />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-medium">Nuova versione disponibile</div>
          <div className="text-xs opacity-80">
            Sono state pubblicate delle novità. Ricarica ora per aggiornare l'app.
          </div>
        </div>
        <button
          data-testid="app-update-apply"
          onClick={applyUpdate}
          disabled={refreshing}
          className="px-4 py-2 rounded-full bg-primary-foreground text-primary text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-60"
        >
          {refreshing ? "Ricarico…" : "Aggiorna ora"}
        </button>
        <button
          data-testid="app-update-dismiss"
          onClick={() => setAvailable(false)}
          className="text-xs opacity-70 hover:opacity-100 underline"
          aria-label="Chiudi"
        >
          Più tardi
        </button>
      </div>
    </div>
  );
}
